import { useEffect, useState } from "react";

type NetworkConnection = {
  effectiveType?: string;
  saveData?: boolean;
  addEventListener?: (type: "change", listener: () => void) => void;
  removeEventListener?: (type: "change", listener: () => void) => void;
  addListener?: (listener: () => void) => void;
  removeListener?: (listener: () => void) => void;
};

type NavigatorWithConnection = Navigator & {
  connection?: NetworkConnection;
  mozConnection?: NetworkConnection;
  webkitConnection?: NetworkConnection;
};

const SLOW_CONNECTION_TYPES = new Set(["slow-2g", "2g", "3g"]);

function getConnection() {
  if (typeof navigator === "undefined") {
    return null;
  }

  const navigatorWithConnection = navigator as NavigatorWithConnection;

  return (
    navigatorWithConnection.connection ??
    navigatorWithConnection.mozConnection ??
    navigatorWithConnection.webkitConnection ??
    null
  );
}

function getReducedDataPreference() {
  const connection = getConnection();

  if (!connection) {
    return false;
  }

  if (connection.saveData) {
    return true;
  }

  return SLOW_CONNECTION_TYPES.has(connection.effectiveType ?? "");
}

function usePrefersReducedData() {
  const [prefersReducedData, setPrefersReducedData] = useState(
    getReducedDataPreference,
  );

  useEffect(() => {
    const connection = getConnection();

    if (!connection) {
      return;
    }

    const onChange = () => {
      setPrefersReducedData(getReducedDataPreference());
    };

    setPrefersReducedData(getReducedDataPreference());

    if (connection.addEventListener && connection.removeEventListener) {
      connection.addEventListener("change", onChange);

      return () => {
        connection.removeEventListener?.("change", onChange);
      };
    }

    connection.addListener?.(onChange);

    return () => {
      connection.removeListener?.(onChange);
    };
  }, []);

  return prefersReducedData;
}

export default usePrefersReducedData;
